import { createSelector } from 'reselect'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import config from 'constants/config'
import { matchPath } from 'react-router'
import { CHECKOUT_HEADER_PATHS } from 'components/App/Routes/constants'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'

/**
 * Once the LuxPlus+ feature is enabled all the places.
 * Should think about refactoring this to use the feature flag directly with
 * useOptimizelyExperiment(OptimizelyFeatureFlags.myFeature)
 * instead of reading it from the redux store.
 */
export const getIsLuxPlusEnabled = (state: App.State): boolean => !!(config.LUXURY_PLUS_ENABLED && state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.luxPlusFeatureToggle])
export const getIsLuxPlusLPPEnabled = (state: App.State): boolean => !!(getIsLuxPlusEnabled(state) && state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.luxPlusLppPricingEnabled])
export const getIsEligibleLuxPlusFreePreview = (state: App.State): boolean => !!(getIsLuxPlusEnabled(state) && state.luxPlus.products.freePreview?.offer?.isEligible)
export const getIsEnabledLuxPlusDiscountedInsurance = (state: App.State): boolean => getIsLuxPlusEnabled(state) && state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.luxPlusDiscountedInsuranceBenefit]
export const getIsEnabledLuxPlusDiscountedBookingProtection = (state: App.State): boolean => getIsLuxPlusEnabled(state) && state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.luxPlusDiscountedBookingProtectionBenefit]
export const getIsEnabledLuxPlusCruiseInclusions = (state: App.State): boolean => getIsLuxPlusEnabled(state) && state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.luxPlusCruiseInclusionsEnabled]
export const getIsEnabledLuxPlusDiscountedExperiences = (state: App.State): boolean => getIsLuxPlusEnabled(state) && state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.luxPlusDiscountedExperiencesBenefit] && isEnabledForFeature(config.LUX_PLUS_EXPERIENCES_REGIONS, state.geo.currentRegionCode)

/**
 * Check if the user has LuxPlus+ membership in the current region.
 * Members are only members in the region they bought the subscription in
 */
const getIsEnabledInCurrentRegionLuxPlusMember = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item,
  (state: App.State) => state.geo.currentRegionCode,
  (isLuxPlusEnabled, memberSubscriptionItem, currentRegion): boolean => {
    return isLuxPlusEnabled && currentRegion === memberSubscriptionItem?.region
  },
)

/**
 * @deprecated if you think you need this selector, you probably don't. Use `getCanRedeemLuxPlusBenefits` instead
 */
export const _isActiveMemberInCurrentRegion = createSelector(
  (state: App.State) => getIsEnabledInCurrentRegionLuxPlusMember(state),
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item,
  (isEnabledInCurrentRegionLuxPlusMember, memberSubscriptionItem): boolean => {
    return isEnabledInCurrentRegionLuxPlusMember && memberSubscriptionItem?.status === 'ACTIVE'
  },
)

export const getIsPendingRenewalLuxPlusMember = createSelector(
  (state: App.State) => getIsEnabledInCurrentRegionLuxPlusMember(state),
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item,
  (isEnabledInCurrentRegionLuxPlusMember, memberSubscriptionItem): boolean => {
    return isEnabledInCurrentRegionLuxPlusMember && memberSubscriptionItem?.status === 'AWAITING_RENEWAL'
  },
)

export const getIsExpiredLuxPlusMemberInGracePeriod = createSelector(
  (state: App.State) => getIsEnabledInCurrentRegionLuxPlusMember(state),
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item,
  (isEnabledInCurrentRegionLuxPlusMember, memberSubscriptionItem): boolean => {
    return isEnabledInCurrentRegionLuxPlusMember && memberSubscriptionItem?.status === 'EXPIRED' && !!memberSubscriptionItem?.isWithinRenewalGracePeriod
  },
)

/**
 * Check if the user is a LuxPlus+ member either subscribed e.g. 'base' tier. awaiting renewal or in free preview.
 * This is used to determine if the user can view but not redeem the benefits of LuxPlus+.
 */
export const getCanViewLuxPlusBenefits = createSelector(
  (state: App.State) => _isActiveMemberInCurrentRegion(state),
  (state: App.State) => getIsPendingRenewalLuxPlusMember(state),
  (state: App.State) => state.auth.account.luxPlus.member.disableMembership,
  (isActiveMemberInCurrentRegion, isAwaitingRenewalInCurrentRegion, disableMembership): boolean => {
    return (isActiveMemberInCurrentRegion || isAwaitingRenewalInCurrentRegion) && !disableMembership
  },
)

export const getIsFreePreviewLuxPlusMember = createSelector(
  (state: App.State) => getIsEnabledInCurrentRegionLuxPlusMember(state),
  (state: App.State) => state.auth.account.luxPlus.member.subscription.item,
  (isEnabledInCurrentRegionLuxPlusMember, memberSubscriptionItem): boolean => {
    return isEnabledInCurrentRegionLuxPlusMember && memberSubscriptionItem?.tier === 'free_preview'
  },
)

/** Requirement to make any LuxPlus+ purchases */
export const getCanRedeemLuxPlusBenefits = createSelector(
  (state: App.State) => getCanViewLuxPlusBenefits(state),
  (state: App.State) => getIsFreePreviewLuxPlusMember(state),
  (state: App.State) => getIsPendingRenewalLuxPlusMember(state),
  (checkCanViewLuxPlusBenefits, isFreePreviewLuxPlusMember, isPendingRenewalLuxPlusMember): boolean => {
    return checkCanViewLuxPlusBenefits && !isFreePreviewLuxPlusMember && !isPendingRenewalLuxPlusMember
  },
)

export const getIsInsuranceAndRefundProtectionLuxPlusBenefitsEnabled = createSelector(
  (state: App.State) => getIsEnabledLuxPlusDiscountedInsurance(state),
  (state: App.State) => getIsEnabledLuxPlusDiscountedBookingProtection(state),
  (isEnabledLuxPlusDiscountedInsurance, isEnabledLuxPlusDiscountedBookingProtection): boolean => {
    return isEnabledLuxPlusDiscountedInsurance && isEnabledLuxPlusDiscountedBookingProtection
  },
)

const freePreviewPathsBlacklist = [
  ...CHECKOUT_HEADER_PATHS,
  '/:region/booking/confirmation',
  `/:region/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`,
  '/:regionCode/campaign/early-bird-2025',
  '/:region/search/map',
]

export const getHasLuxPlusFreePreviewBanner = createSelector(
  (state: App.State) => getIsLuxPlusEnabled(state),
  (state: App.State) => getCanViewLuxPlusBenefits(state),
  (state: App.State) => state.system.luxPlusFreePreviewBannerDismissed,
  (state: App.State) => state.router.location.pathname,
  (
    isLuxPlusEnabled,
    canViewLuxPlusBenefits,
    luxPlusFreePreviewBannerDismissed,
    path,
  ): boolean => {
    const isBlacklistedPath = !!matchPath(path, {
      path: freePreviewPathsBlacklist,
      exact: true,
    })

    return isLuxPlusEnabled && !canViewLuxPlusBenefits && !luxPlusFreePreviewBannerDismissed && !isBlacklistedPath
  },
)

const pendingRenewalPathsBlacklist = [
  ...CHECKOUT_HEADER_PATHS,
  `/:region/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`,
  '/:region/booking/confirmation',
]

export const getHasLuxPlusPendingRenewalBanner = createSelector(
  (state: App.State) => getIsPendingRenewalLuxPlusMember(state),
  (state: App.State) => state.router.location.pathname,
  (
    isPendingRenewalLuxPlusMember,
    path,
  ): boolean => {
    const isBlacklistedPath = !!matchPath(path, {
      path: pendingRenewalPathsBlacklist,
      exact: false,
    })

    return isPendingRenewalLuxPlusMember && !isBlacklistedPath
  },
)
